import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import type {
  AdminGuardianInviteDto,
  AdminGuardianInvitesResponseDto,
  AdminStudentsResponseDto,
  CreateAdminGuardianInviteRequestDto,
  CreateAdminGuardianInviteResponseDto,
  FamilyAccountsResponseDto,
  InviteScopeType,
  RelationType
} from "@madamuh/domain";
import { Link, useNavigate, useParams } from "react-router-dom";
import { fetchAdminStudents, fetchFamilyAccounts } from "../../shared/api/adminRoster";
import {
  createAdminGuardianInvite,
  fetchAdminGuardianInvites,
  reissueAdminGuardianInvite,
  revokeAdminGuardianInvite
} from "../../shared/api/guardian";
import {
  getProtectedApiErrorMessage,
  isUnauthenticatedApiError
} from "../../shared/session/protectedApiError";
import { resolveInviteTenantName } from "../../shared/lib/inviteShare";
import { formatTenantDateTime } from "../../shared/lib/tenantDateTime";
import { describeRelationType, describeStatus } from "../../shared/lib/uiLabels";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { InviteSharePanel } from "../../shared/ui/InviteSharePanel";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";

const adminLinks = [
  { to: "/t/:tenantSlug/teacher/admin", label: "管理ホーム" },
  { to: "/t/:tenantSlug/teacher/admin/guardian-link-requests", label: "申請一覧" },
  { to: "/t/:tenantSlug/link-student", label: "保護者側導線" }
];

type InviteFormState = {
  scopeType: InviteScopeType;
  studentId: string;
  familyAccountId: string;
  relationTypeDefault: RelationType;
  maxUseCount: string;
  expiresAt: string;
};

function createDefaultInviteExpiryInput(now = new Date()): string {
  const next = new Date(now);
  next.setDate(next.getDate() + 30);
  next.setSeconds(0, 0);

  const year = next.getFullYear();
  const month = String(next.getMonth() + 1).padStart(2, "0");
  const day = String(next.getDate()).padStart(2, "0");
  const hour = String(next.getHours()).padStart(2, "0");
  const minute = String(next.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hour}:${minute}`;
}

function createInitialInviteFormState(): InviteFormState {
  return {
    scopeType: "student",
    studentId: "",
    familyAccountId: "",
    relationTypeDefault: "parent",
    maxUseCount: "1",
    expiresAt: createDefaultInviteExpiryInput()
  };
}

function buildCreateInviteRequest(form: InviteFormState): CreateAdminGuardianInviteRequestDto {
  return {
    scopeType: form.scopeType,
    studentId: form.scopeType === "student" ? form.studentId || null : null,
    familyAccountId: form.scopeType === "family" ? form.familyAccountId || null : null,
    relationTypeDefault: form.relationTypeDefault,
    maxUseCount: form.maxUseCount.trim() ? Number.parseInt(form.maxUseCount, 10) : null,
    expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : null
  };
}

function getInviteScopeSummary(invite: AdminGuardianInviteDto): string {
  if (invite.scopeType === "family") {
    return invite.familyAccountDisplayName ?? invite.familyAccountId ?? "家族単位招待";
  }

  return invite.students.map((student) => student.displayName).join(", ") || invite.studentIds.join(", ");
}

function getInviteLinkSummary(invite: AdminGuardianInviteDto): string {
  if (invite.activeLinkCount === 0) {
    return invite.usedCount > 0 ? "招待は消費済み / 有効な紐付けはありません" : "有効な紐付けはありません";
  }

  return `有効な紐付け ${invite.activeLinkCount} 件: ${invite.activeLinkedStudents
    .map((student) => student.displayName)
    .join(", ")}`;
}

export function AdminGuardianLinksPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [invites, setInvites] = useState<AdminGuardianInvitesResponseDto | null>(null);
  const [students, setStudents] = useState<AdminStudentsResponseDto | null>(null);
  const [familyAccounts, setFamilyAccounts] = useState<FamilyAccountsResponseDto | null>(null);
  const [inviteForm, setInviteForm] = useState<InviteFormState>(createInitialInviteFormState());
  const [latestIssuedInvite, setLatestIssuedInvite] = useState<CreateAdminGuardianInviteResponseDto | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));
  const inviteTenantName = resolveInviteTenantName({
    displayName: session?.tenant.displayName,
    tenantSlug
  });

  async function refreshPage() {
    setLoadState("loading");
    setLoadError(null);

    try {
      const [nextInvites, nextStudents, nextFamilyAccounts] = await Promise.all([
        fetchAdminGuardianInvites(tenantSlug),
        fetchAdminStudents(tenantSlug),
        fetchFamilyAccounts(tenantSlug)
      ]);

      setInvites(nextInvites);
      setStudents(nextStudents);
      setFamilyAccounts(nextFamilyAccounts);
      setLoadState("idle");
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadState("error");
      setLoadError(getProtectedApiErrorMessage(error, "保護者招待一覧の取得に失敗しました"));
    }
  }

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole) {
      return;
    }

    void refreshPage();
  }, [hasAdminRole, isSessionReady, tenantSlug]);

  async function handleCreateInvite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusyKey("create-invite");
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      const created = await createAdminGuardianInvite(tenantSlug, buildCreateInviteRequest(inviteForm));
      setLatestIssuedInvite(created);
      setFeedbackMessage(`保護者招待を作成しました: ${created.invite.inviteId}`);
      setInviteForm((current) => ({
        ...createInitialInviteFormState(),
        scopeType: current.scopeType,
        maxUseCount: current.scopeType === "student" ? "1" : "2"
      }));
      await refreshPage();
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "保護者招待の作成に失敗しました"));
    } finally {
      setBusyKey(null);
    }
  }

  async function handleRevoke(inviteId: string) {
    setBusyKey(`revoke:${inviteId}`);
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      const revoked = await revokeAdminGuardianInvite(tenantSlug, inviteId);
      setFeedbackMessage(`招待を失効しました: ${revoked.inviteId}`);
      await refreshPage();
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "保護者招待の失効に失敗しました"));
    } finally {
      setBusyKey(null);
    }
  }

  async function handleReissue(inviteId: string) {
    setBusyKey(`reissue:${inviteId}`);
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      const reissued = await reissueAdminGuardianInvite(tenantSlug, inviteId);
      setLatestIssuedInvite(reissued);
      setFeedbackMessage(`保護者招待を再発行しました: ${reissued.invite.inviteId}`);
      await refreshPage();
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "保護者招待の再発行に失敗しました"));
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <OperatorPageScaffold
      currentArea="admin"
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      title="保護者紐付け・招待"
      description="保護者向け招待の発行、利用状況、現在の紐付け状態を確認します。"
      links={adminLinks}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {routeStatus === "checking" || sessionStatus === "loading" ? <p>セッションを確認しています。</p> : null}
        {routeStatus === "redirecting" ? <p>利用開始へ移動しています。</p> : null}
        {sessionStatus === "error" ? <p>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !hasAdminRole ? <p>この画面は管理者のみ利用できます。</p> : null}
        {loadState === "loading" ? <p>招待情報と生徒一覧を読み込んでいます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}
        {feedbackMessage ? <p>{feedbackMessage}</p> : null}

        {isSessionReady && hasAdminRole ? (
          <>
            <section style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>招待を作成する</h2>
              <p style={{ margin: 0, fontSize: 13, opacity: 0.8 }}>
                FamilyAccount は家族グループ設定です。保護者の閲覧権限を解除するには、生徒詳細で保護者紐付けを解除してください。
              </p>
              <form
                onSubmit={(event) => void handleCreateInvite(event)}
                style={{ display: "grid", gap: 12, padding: 12, border: "1px solid #e5e5e5", borderRadius: 12 }}
              >
                <label style={{ display: "grid", gap: 4 }}>
                  <span>招待範囲</span>
                  <select
                    value={inviteForm.scopeType}
                    onChange={(event) =>
                      setInviteForm((current) => ({
                        ...current,
                        scopeType: event.target.value === "family" ? "family" : "student",
                        maxUseCount: event.target.value === "family" ? "2" : "1"
                      }))
                    }
                  >
                    <option value="student">生徒ごと</option>
                    <option value="family">家族ごと</option>
                  </select>
                </label>

                {inviteForm.scopeType === "student" ? (
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>対象生徒</span>
                    <select
                      value={inviteForm.studentId}
                      onChange={(event) =>
                        setInviteForm((current) => ({ ...current, studentId: event.target.value }))
                      }
                    >
                      <option value="">選択してください</option>
                      {students?.items.map((student) => (
                        <option key={student.studentId} value={student.studentId}>
                          {student.displayName} / {student.studentId}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : (
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>対象FamilyAccount</span>
                    <select
                      value={inviteForm.familyAccountId}
                      onChange={(event) =>
                        setInviteForm((current) => ({ ...current, familyAccountId: event.target.value }))
                      }
                    >
                      <option value="">選択してください</option>
                      {familyAccounts?.items.map((familyAccount) => (
                        <option key={familyAccount.familyAccountId} value={familyAccount.familyAccountId}>
                          {familyAccount.displayName} / {familyAccount.familyAccountId}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                <label style={{ display: "grid", gap: 4 }}>
                  <span>続柄</span>
                  <select
                    value={inviteForm.relationTypeDefault}
                    onChange={(event) =>
                      setInviteForm((current) => ({
                        ...current,
                        relationTypeDefault: event.target.value === "guardian" ? "guardian" : "parent"
                      }))
                    }
                  >
                    <option value="parent">保護者</option>
                    <option value="guardian">ご家族</option>
                  </select>
                </label>

                <label style={{ display: "grid", gap: 4 }}>
                  <span>利用回数上限</span>
                  <input
                    type="number"
                    min={1}
                    value={inviteForm.maxUseCount}
                    onChange={(event) =>
                      setInviteForm((current) => ({ ...current, maxUseCount: event.target.value }))
                    }
                  />
                </label>

                <label style={{ display: "grid", gap: 4 }}>
                  <span>有効期限</span>
                  <input
                    type="datetime-local"
                    value={inviteForm.expiresAt}
                    onChange={(event) =>
                      setInviteForm((current) => ({ ...current, expiresAt: event.target.value }))
                    }
                  />
                </label>

                <button type="submit" disabled={busyKey === "create-invite"}>
                  {busyKey === "create-invite" ? "作成中..." : "招待を作成する"}
                </button>
              </form>
            </section>

            {latestIssuedInvite ? (
              <section style={{ display: "grid", gap: 12 }}>
                <InviteSharePanel
                  tenantName={inviteTenantName}
                  inviteKind={latestIssuedInvite.invite.scopeType === "family" ? "guardianFamily" : "guardianStudent"}
                  primaryUrl={latestIssuedInvite.delivery.inviteUrl}
                  inviteId={latestIssuedInvite.invite.inviteId}
                />
                <label style={{ display: "grid", gap: 4 }}>
                  <span>手入力コード</span>
                  <input readOnly value={latestIssuedInvite.delivery.manualCode} />
                </label>
                <div style={{ fontSize: 14, opacity: 0.8 }}>
                  保護者側では <code>/t/{tenantSlug}/link-student</code> から手入力コードを入力するか、上の招待URLを開いて連携できます。
                </div>
              </section>
            ) : null}

            <section style={{ display: "grid", gap: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                <h2 style={{ margin: 0, fontSize: 18 }}>招待一覧</h2>
                <Link to={`/t/${tenantSlug}/teacher/admin/guardian-link-requests`}>申請レビューへ</Link>
              </div>

              {invites?.items.length === 0 ? <p style={{ margin: 0 }}>招待はまだありません。</p> : null}

              <div style={{ display: "grid", gap: 12 }}>
                {invites?.items.map((invite) => {
                  const isRevokeBusy = busyKey === `revoke:${invite.inviteId}`;
                  const isReissueBusy = busyKey === `reissue:${invite.inviteId}`;

                  return (
                    <article
                      key={invite.inviteId}
                      style={{ border: "1px solid #e5e5e5", borderRadius: 12, padding: 12, display: "grid", gap: 8 }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                        <div style={{ display: "grid", gap: 4 }}>
                          <strong>{getInviteScopeSummary(invite)}</strong>
                          <span style={{ fontSize: 13, opacity: 0.8 }}>{invite.inviteId}</span>
                        </div>
                        <span
                          style={{
                            fontSize: 12,
                            padding: "4px 8px",
                            borderRadius: 999,
                            background:
                              invite.status === "active"
                                ? "#eef6ff"
                                : invite.status === "revoked"
                                  ? "#f3f3f3"
                                  : "#fff4e8"
                          }}
                        >
                          {describeStatus(invite.status)}
                        </span>
                      </div>

                      <div>範囲: {describeStatus(invite.scopeType)}</div>
                      <div>続柄: {describeRelationType(invite.relationTypeDefault)}</div>
                      <div>対象生徒: {invite.students.map((student) => student.displayName).join(", ") || "なし"}</div>
                      <div>FamilyAccount: {invite.familyAccountDisplayName ?? invite.familyAccountId ?? "なし"}</div>
                      <div>
                        使用回数: {invite.usedCount} / {invite.maxUseCount}
                      </div>
                      <div>有効期限: {formatTenantDateTime(invite.expiresAt)}</div>
                      <div>最終使用日時: {formatTenantDateTime(invite.lastConsumedAt)}</div>
                      <div>{getInviteLinkSummary(invite)}</div>

                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button
                          type="button"
                          onClick={() => void handleReissue(invite.inviteId)}
                          disabled={isReissueBusy}
                        >
                          {isReissueBusy ? "再発行中..." : "再発行"}
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleRevoke(invite.inviteId)}
                          disabled={isRevokeBusy || invite.status === "revoked"}
                        >
                          {isRevokeBusy ? "失効中..." : "失効"}
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          </>
        ) : null}
      </div>
    </OperatorPageScaffold>
  );
}
